import type { PhilosophyPosture } from 'api/recoveryAi.types'

// Stance cards render most→least generous (reverse of the wire `POSTURES`
// order). The wire/enum value (frugal|balanced|generous|whatever_it_takes) is
// NEVER reordered — this is a display-only ordering (AI-29 / D2).
export const STANCE_ORDER: PhilosophyPosture[] = [
  'whatever_it_takes',
  'generous',
  'balanced',
  'frugal',
]

// Default "RECOMMENDED FOR YOU" posture when there is no assessment-driven
// recommendation (the Settings pane has no assessment; the wizard falls back
// to this when `state.recommendedPosture` is null).
export const DEFAULT_RECOMMENDED_POSTURE: PhilosophyPosture = 'generous'

// AI-1179: the decided (8/27) "More likely to:" / "Less likely to:" bullets
// have a DIFFERENT count per posture (Cautious 2/2, Balanced 4/2, Generous
// 3/2, Whatever it takes 3/2) — react-redux-i18n cannot store arrays, so each
// posture's locale keys are enumerated `<posture>_more_1..N` /
// `<posture>_less_1..N` (see `recovery_ai.wizard.philosophy` in the locale
// file). This map is the single source of truth for which keys exist per
// posture, so neither GenerositySliderBody nor StanceCardList/StanceCard can
// probe past the real count and render a raw, untranslated key path.
export const POSTURE_BULLET_COUNTS: Record<
  PhilosophyPosture,
  { more: number; less: number }
> = {
  frugal: { more: 2, less: 2 },
  balanced: { more: 4, less: 2 },
  generous: { more: 3, less: 2 },
  whatever_it_takes: { more: 3, less: 2 },
}

// Build the enumerated bullet keys for a posture's "More likely to:" /
// "Less likely to:" columns. Keeps the locale-key wiring in one place so both
// the wizard step and the Settings pane stay in sync.
export function expectBulletKeys(posture: PhilosophyPosture): string[] {
  const { more } = POSTURE_BULLET_COUNTS[posture]
  return Array.from({ length: more }, (_, i) => `${posture}_more_${i + 1}`)
}

export function wontBulletKeys(posture: PhilosophyPosture): string[] {
  const { less } = POSTURE_BULLET_COUNTS[posture]
  return Array.from({ length: less }, (_, i) => `${posture}_less_${i + 1}`)
}
